import { Icon } from '@studio/design-system/icon';
import {
  getCurrentVendor,
  getCurrentVendorBadgeIcon,
  Vendor,
  vendorName
} from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { resolveBannerDisplay } from './iris-enabled-banner.helpers.js';
import type { IrisEnabledBannerProps } from './types.js';
import { Sel, STagNew } from '#~/stag/index.js';

/**
 * Iris-enabled banner component with vendor-aware styling.
 *
 * Iris: rainbow gradient border, single-line text.
 * Hive: solid orange border, title + body text.
 *
 * All vendor-branching lives in `iris-enabled-banner.helpers.ts` so it can
 * be unit-tested without a React renderer (ai-code-requires-tests, global).
 */
export const IrisEnabledBanner = ({
  visible,
  onClose,
  title,
  text,
  logoSrc,
  logoAlt,
  className,
  ...props
}: IrisEnabledBannerProps): ReactElement | null => {
  if (!visible) {
    return null;
  }

  const { displayLogoSrc, displayLogoAlt, displayTitle, displayText, borderStyle, innerBg } =
    resolveBannerDisplay({
      isIris: getCurrentVendor() === Vendor.IRIS,
      fallbackLogoSrc: getCurrentVendorBadgeIcon(),
      fallbackLogoAlt: `${vendorName()} Logo`,
      logoSrc,
      logoAlt,
      title,
      text
    });

  return (
    // Stable stag on the banner container so the HIL popup-watch can address it
    // (the container otherwise carries no role/stag; only the close button has an
    // aria-label). Per rule: playwright-stag-selectors-only (ai.knowledge/global).
    <div
      className={twMerge('relative rounded-4', className)}
      style={borderStyle}
      {...props}
      {...STagNew(Sel.Notifications, 'iris_enabled_banner')}
    >
      <div className={twMerge('flex items-center gap-8 rounded-4 px-12 py-8', innerBg)}>
        <img
          src={displayLogoSrc}
          alt={displayLogoAlt}
          className='h-[14px] w-[14px] flex-shrink-0'
        />

        <div className='flex-1'>
          {displayTitle && (
            <p className='text-sm font-medium leading-[1.26] text-light'>{displayTitle}</p>
          )}
          <p className='text-sm font-regular leading-[1.26] text-[#A9B3C6]'>{displayText}</p>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className='flex h-12 w-12 cursor-pointer items-center justify-center rounded-4 transition-colors hover:bg-base-200'
            aria-label='Close banner'
          >
            <Icon icon='close' className='h-12 w-12 text-light' />
          </button>
        )}
      </div>
    </div>
  );
};

IrisEnabledBanner.displayName = 'IrisEnabledBanner';
